<script>
    import {twMerge} from "$m7-library/lib/twMerge";
    import { DownIcon } from "$m7-library/main/index.js";
    import { t } from "$m7-library/utils/lang.js";

    let {
        label = "",
        value = $bindable(),
        options = [],
        placeholder = t("Select an option..."),
        prefix = "",
        class: cls = "",
        optionClass = "",
        disabled = false,
        onchange = () => {},
        initialized = true,
        load = async () => {},
        name = "",
        // Optional per-option attribute extension. Caller returns an object of
        // extra HTML attributes to spread onto the rendered option button.
        // Use for domain-specific selectors, e.g. data-country-option={code}.
        itemAttrs = (_option) => ({}),
        // Classes for the label text itself, separate from `class`, which
        // styles the wrapper. Needed wherever the control sits on a dark
        // background: the wrapper's colour is inherited by the button too, so
        // colouring the label through `class` would take the button with it.
        labelClass = "",
    } = $props();

    let isOpen = $state(false);
    let wrapperEl;
    let listEl;
    let loading = $state(false);

    const labelCls = $derived(twMerge("tw:block tw:text-sm tw:relative tw:text-cyprus", cls));
    const optionCls = $derived(twMerge(
        "tw:block tw:cursor-pointer tw:py-2 tw:px-4 tw:w-full tw:hover:font-bold",
        optionClass,
    ));

    const selectedLabel = $derived(
        options.find((opt) => opt.value === value)?.label || placeholder,
    );

    function handleSelect(optionValue) {
        if (disabled) return;
        onchange(optionValue);
        value = optionValue;
        isOpen = false;
    }

    function open(toggle = false) {
        if (disabled) return;
        if (toggle) {
            isOpen = !isOpen;
        } else {
            isOpen = true;
        }
        if (!initialized) {
            loading = true;
            return load().then(() => {
                initialized = true;
                loading = false;
            });
        }
    }

    function handleKeydown(event) {
        if (disabled) return;
        switch (event.key) {
            case "Enter":
            case " ":
                event.preventDefault();
                open(true);
                break;
            case "Escape":
                isOpen = false;
                break;
            case "ArrowDown":
            case "ArrowUp":
                event.preventDefault();
                if (!isOpen) {
                    open();
                    return;
                }
                const currentIndex = options.findIndex(
                    (opt) => opt.value === value,
                );
                const direction = event.key === "ArrowDown" ? 1 : -1;
                let nextIndex = currentIndex + direction;

                if (nextIndex < 0) nextIndex = options.length - 1;
                if (nextIndex >= options.length) nextIndex = 0;

                value = options[nextIndex].value;
                break;
        }
    }

    // Effect to handle clicks outside the component to close it
    $effect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            // Use composedPath() to get the real event target, even across Shadow DOM boundaries.
            const target = event.composedPath()[0] || event.target;
            if (wrapperEl && !wrapperEl.contains(target)) {
                isOpen = false;
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        // Cleanup function
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    });

    // Effect to scroll active item into view
    $effect(() => {
        if (!isOpen || !value || !listEl) return;

        const optionEl = listEl.querySelector(
            `li[role="option"][data-value="${value}"]`,
        );

        if (!optionEl) return;

        // Scroll the list, never the page. scrollIntoView walks up to the
        // nearest scrollable ancestor, and a list shorter than its max-height
        // is not scrollable — so the browser used to centre the option by
        // scrolling the document instead, yanking the whole widget out of
        // view on a plain dropdown click. Setting scrollTop cannot reach past
        // the list.
        listEl.scrollTop =
            optionEl.offsetTop - (listEl.clientHeight - optionEl.clientHeight) / 2;
    });
</script>

<label bind:this={wrapperEl} class={labelCls} data-select-name={name} data-selected-value={value}>
    {#if label}<span class={labelClass}>{label}</span>
    {/if}
    <button
        class="tw:block tw:w-full tw:text-left tw:relative tw:cursor-pointer tw:rounded-lg tw:bg-grey tw:py-2 tw:pl-3 tw:pr-9 {disabled
            ? ' tw:cursor-not-allowed'
            : ''}"
        type="button"
        onclick={() => open(true)}
        onkeydown={handleKeydown}
        {disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-checkout-action={name ? 'open-select-' + name : undefined}
    >
        <span class="tw:block tw:truncate"
            >{#if prefix}{prefix}
            {/if}{@html selectedLabel}</span
        >
        <span class="tw:absolute tw:inset-y-0 tw:right-0 tw:flex tw:items-center tw:pr-3"
            ><DownIcon class="tw:w-3 tw:h-3" /></span
        >
    </button>
    {#if isOpen}
        <!-- shadow-lg, not a custom token: the option list floats over
             whatever the page put underneath, and on a light card its own
             near-white background alone leaves no visible edge. A built-in
             keeps it working in every consuming app — the shadow tokens those
             apps define are named differently in each. -->
        <ul
            bind:this={listEl}
            class="tw:absolute tw:z-10 tw:max-h-60 tw:w-full tw:overflow-auto tw:rounded-md tw:mt-1 tw:py-1 tw:bg-grey tw:shadow-lg"
            role="listbox"
        >
            {#if loading}
                <li class={optionCls}>...loading</li>
            {:else}
                {#each options as option (option.value)}
                    <li
                        class="tw:relative tw:p-0"
                        role="option"
                        aria-selected={value === option.value}
                        data-value={option.value}
                    >
                        <button
                            class={optionCls}
                            class:font-bold={value === option.value}
                            data-checkout-action="select-option"
                            data-option-value={option.value}
                            {...itemAttrs(option)}
                            onmousedown={(e) => {
                                e.stopPropagation();
                                handleSelect(option.value);
                            }}>{option.label}</button
                        >
                    </li>
                {/each}
            {/if}
        </ul>
    {/if}
</label>
